// Quelle: /opt/lilasprojekte/frontend/src/components/ui/Icon.tsx (Stand 2026-09-29)
// Geändert: die Registry enthält GENAU die Namen aus SPEC 03 §3.4 (statisch, kein Lazy-Split,
// DESIGN §2); alles andere wörtlich (unbekannter Name = unsichtbarer Platzhalter + Warnung).
import type { CSSProperties } from "react";
import type { Icon as PhosphorIcon, IconWeight } from "@phosphor-icons/react";
import {
  ArrowCounterClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  ArrowUUpLeft,
  ArrowUpRight,
  ArrowsClockwise,
  ArrowsIn,
  ArrowsOut,
  Cards,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChartBar,
  ChartDonut,
  Check,
  CheckCircle,
  CheckSquareOffset,
  ClipboardText,
  Clock,
  Copy,
  Cpu,
  Crop,
  Database,
  DotsSixVertical,
  DotsThree,
  DownloadSimple,
  Eraser,
  Export,
  Eye,
  EyeSlash,
  FileCsv,
  FileDoc,
  FilePdf,
  FilePpt,
  FileXls,
  Flag,
  FrameCorners,
  Funnel,
  FunnelSimple,
  FunnelX,
  Gauge,
  Gear,
  GridFour,
  HandWaving,
  House,
  Hourglass,
  Image,
  Info,
  Key,
  Lightning,
  ListChecks,
  LockSimple,
  MagnifyingGlass,
  MapPin,
  MapTrifold,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Minus,
  Moon,
  Notebook,
  PaperPlaneTilt,
  Pause,
  PencilSimple,
  Play,
  Plus,
  PlusCircle,
  Quotes,
  Rectangle,
  Rows,
  Scan,
  Scissors,
  Selection,
  ShieldCheck,
  SignOut,
  Sliders,
  Sparkle,
  Stack,
  Star,
  Stop,
  Sun,
  Table,
  Tag,
  Trash,
  TrayArrowUp,
  UploadSimple,
  User,
  UserCircle,
  Users,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Eine FESTE, geschlossene Registry — kein `import * as Phosphor`.
//
// Jedes Phosphor-Modul trägt alle sechs Strichstärken als eingebettete
// React-Elemente in EINEM `new Map([...])`-Literal. Weil das ein atomarer
// Ausdruck ist, kann der Bündler die ungenutzten Stärken nicht wegschütteln:
// rund 3,3 KB roh je Symbol, gleich wie viele Stärken wirklich gezeichnet
// werden. Ein Namensraum-Import zöge den ganzen Satz (1 500+ Symbole) ins
// Bündel. Diese Liste liegt über `manualChunks` in einem eigenen Teil
// (`phosphor`, vite.config.ts), der sich nie ändert.
//
// WER EIN SYMBOL ERGÄNZT, ergänzt es HIER — und nirgends sonst (und in SPEC 03
// §3.4). Die Namen sind die Phosphor-Namen in Bindestrichschreibung, damit ein
// Aufrufer sie aus der Dokumentation abschreiben kann. `weight="fill"` nur für
// sparkle (Chat), star (angeheftet) und die Meldungssymbole.
// ---------------------------------------------------------------------------
const REGISTRY = {
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up-right": ArrowUpRight,
  "arrow-counter-clockwise": ArrowCounterClockwise,
  "arrows-clockwise": ArrowsClockwise,
  "arrows-out": ArrowsOut,
  "arrows-in": ArrowsIn,
  "arrow-square-out": ArrowSquareOut,
  "caret-down": CaretDown,
  "caret-up": CaretUp,
  "caret-left": CaretLeft,
  "caret-right": CaretRight,
  "cards": Cards,
  "chart-bar": ChartBar,
  "chart-donut": ChartDonut,
  "grid-four": GridFour,
  "table": Table,
  "check": Check,
  "check-circle": CheckCircle,
  "check-square-offset": CheckSquareOffset,
  "clipboard-text": ClipboardText,
  "copy": Copy,
  "download-simple": DownloadSimple,
  "dots-three": DotsThree,
  "dots-six-vertical": DotsSixVertical,
  "export": Export,
  "eye": Eye,
  "eye-slash": EyeSlash,
  "file-csv": FileCsv,
  "file-xls": FileXls,
  "file-doc": FileDoc,
  "file-pdf": FilePdf,
  "file-ppt": FilePpt,
  "funnel": Funnel,
  "funnel-simple": FunnelSimple,
  "funnel-x": FunnelX,
  "gear": Gear,
  "hand-waving": HandWaving,
  "hourglass": Hourglass,
  "image": Image,
  "info": Info,
  "key": Key,
  "lightning": Lightning,
  "lock-simple": LockSimple,
  "magnifying-glass": MagnifyingGlass,
  "minus": Minus,
  "moon": Moon,
  "sun": Sun,
  "notebook": Notebook,
  "paper-plane-tilt": PaperPlaneTilt,
  "pause": Pause,
  "play": Play,
  "plus": Plus,
  "plus-circle": PlusCircle,
  "quotes": Quotes,
  "rectangle": Rectangle,
  "scan": Scan,
  "selection": Selection,
  "shield-check": ShieldCheck,
  "sign-out": SignOut,
  "sliders": Sliders,
  "sparkle": Sparkle,
  "stack": Stack,
  "star": Star,
  "stop": Stop,
  "tag": Tag,
  "trash": Trash,
  "tray-arrow-up": TrayArrowUp,
  "upload-simple": UploadSimple,
  "user": User,
  "user-circle": UserCircle,
  "users": Users,
  "warning": Warning,
  "warning-circle": WarningCircle,
  "x": X,
  "x-circle": XCircle,
  "arrow-u-up-left": ArrowUUpLeft,
  "crop": Crop,
  "frame-corners": FrameCorners,
  "magnifying-glass-plus": MagnifyingGlassPlus,
  "magnifying-glass-minus": MagnifyingGlassMinus,
  "rows": Rows,
  "pencil-simple": PencilSimple,
  "flag": Flag,
  "clock": Clock,
  "cpu": Cpu,
  "eraser": Eraser,
  "scissors": Scissors,
  "list-checks": ListChecks,
  "gauge": Gauge,
  // E21 Verortung (05 §10): Ort, Karte/Ortsverzeichnis, Wohnort.
  "map-pin": MapPin,
  "map-trifold": MapTrifold,
  "house": House,
  // E29 Datenquellen (05 §18): Verwaltungsreiter und „Aus Datenbank laden“.
  "database": Database,
} satisfies Record<string, PhosphorIcon>;

/** Alle Namen, die `<Icon name="…">` kennt. */
export type IconName = keyof typeof REGISTRY;
export const ICON_NAMEN = Object.keys(REGISTRY) as IconName[];

export interface IconProps {
  /** Ein Name aus der Registry. Absichtlich `string`: ein Aufrufer soll einen
   *  Namen aus einer Tabelle durchreichen dürfen. */
  name: string;
  /** Strichstärke; Vorgabe "regular". Kräftige Stärken sind Ausnahmen. */
  weight?: IconWeight;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** wird zum barrierefreien Namen; ohne ihn ist das Symbol reine Zier. */
  title?: string;
}

export function Icon({ name, weight = "regular", size = 18, className = "", style = {}, title }: IconProps) {
  const Cmp = (REGISTRY as Record<string, PhosphorIcon | undefined>)[name];

  if (!Cmp) {
    // Ein vertippter Name fällt nicht beim Übersetzen auf, sondern als leere
    // Stelle im Bild. In der Entwicklung deshalb einmal laut werden; im Bau
    // bleibt ein maßhaltiger, unsichtbarer Platzhalter, damit die Zeile nicht
    // um die Breite des fehlenden Symbols zusammenrutscht.
    if (import.meta.env.DEV) console.warn(`[Icon] Name nicht in der Registry: ${name}`);
    return <span aria-hidden style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle", flex: "none" }} />;
  }

  return (
    <Cmp
      size={size}
      weight={weight}
      className={className}
      style={{ verticalAlign: "middle", flex: "none", ...style }}
      aria-hidden={title ? undefined : true}
      alt={title}
    />
  );
}
